import { Button } from '@strata/react-buttons';
import { FormSection } from '@strata/react-form-section';
import { InlineMessage } from '@strata/react-inline-message';
import { Timeline } from '@strata/react-timeline';
import { useFormikContext } from 'formik';
import { type ComponentProps } from 'react';

import { Documents } from 'components/intakeCases/Documents';
import { FaxStatuses } from 'components/intakeCases/FaxStatus';
import { PatientInputs } from 'components/intakeForm/PatientInputs';
import { PrescriberInputs } from 'components/intakeForm/PrescriberInputs';
import { ProductInputs } from 'components/intakeForm/ProductInputs';
import { SectionCompletionStatus } from 'components/intakeForm/SectionCompletionStatus';
import { type IntakeCase, type MappedMilestone } from 'store/api/intakeCaseApi';
import { FormSections, FormSectionTitleMap } from 'store/intakeForm/intakeFormSchema';

const UnverifiedMemberInlineMsg = () => (
    <InlineMessage
        intent={InlineMessage.Intent.ERROR}
        label="The patient associated to this intake case is not verified."
    />
);

const UnverifiedPrescriberInlineMsg = () => (
    <InlineMessage
        intent={InlineMessage.Intent.WARNING}
        label="The prescriber associated to this intake case is not verified."
    />
);

interface OverviewTabProps {
    intakeCaseDetails?: IntakeCase;
    intakeCaseDetailsFetching: boolean;
    inputsDisabled: boolean;
    intakeCaseMilestonesData: MappedMilestone[];
    intakeCaseId: string;
}

export const OverviewTab = ({
    intakeCaseDetails = undefined,
    intakeCaseDetailsFetching,
    inputsDisabled,
    intakeCaseMilestonesData,
    intakeCaseId,
}: OverviewTabProps) => {
    const { values, isValid, dirty, handleSubmit, isSubmitting } = useFormikContext<IntakeCase>();

    const saveDisabled = inputsDisabled || isSubmitting || !(isValid && dirty);

    return (
        <div className="sds_intake-case-details">
            <div className="sds_intake-case-details__column" style={{ flexGrow: 2, gap: 0 }}>
                <FormSection
                    className="sds_intake-form-section"
                    sectionTitle="Patient"
                    isLoading={intakeCaseDetailsFetching}
                    rightHeaderContent={
                        <SectionCompletionStatus section={FormSections.PATIENT} values={values} />
                    }
                    content={
                        <>
                            {values.memberVerified === false && (
                                <div className="margin__bottom--medium">
                                    <UnverifiedMemberInlineMsg />
                                </div>
                            )}
                            <PatientInputs disabled={inputsDisabled} />
                        </>
                    }
                />
                <FormSection
                    className="sds_intake-form-section"
                    sectionTitle={FormSectionTitleMap[FormSections.PRODUCT]}
                    rightHeaderContent={
                        <SectionCompletionStatus section={FormSections.PRODUCT} values={values} />
                    }
                    isLoading={intakeCaseDetailsFetching}
                    content={
                        <ProductInputs segmentId={values.segmentId} disabled={inputsDisabled} />
                    }
                />
                <FormSection
                    className="sds_intake-form-section"
                    sectionTitle={FormSectionTitleMap[FormSections.PRESCRIBER]}
                    rightHeaderContent={
                        <SectionCompletionStatus
                            section={FormSections.PRESCRIBER}
                            values={values}
                        />
                    }
                    isLoading={intakeCaseDetailsFetching}
                    content={
                        <>
                            {values.prescriberVerified === false && (
                                <div className="margin__bottom--medium">
                                    <UnverifiedPrescriberInlineMsg />
                                </div>
                            )}
                            <PrescriberInputs disabled={inputsDisabled} />
                        </>
                    }
                />
                {!inputsDisabled && (
                    <div className="sds_form-container__footer">
                        <Button
                            id="save-intake-case-button"
                            intent={Button.Intent.PRIMARY}
                            label="Save"
                            type="submit"
                            onClick={() => handleSubmit()}
                            disabled={saveDisabled}
                        />
                    </div>
                )}
            </div>
            <div className="sds_intake-case-details__column">
                <Documents
                    intakeCaseDetails={intakeCaseDetails}
                    isLoading={intakeCaseDetailsFetching}
                />
                <FaxStatuses
                    intakeCaseId={intakeCaseId}
                    monitoringCase={intakeCaseDetails?.monitoringCase}
                />
                <div className="sds_details-page-overview__container" style={{ flex: '1 0' }}>
                    <FormSection
                        sectionTitle="Intake Case Timeline"
                        isLoading={!intakeCaseMilestonesData}
                        content={
                            <Timeline
                                sectionDataArray={
                                    intakeCaseMilestonesData as ComponentProps<
                                        typeof Timeline
                                    >['sectionDataArray']
                                }
                            />
                        }
                    />
                </div>
            </div>
        </div>
    );
};
